import type { AttendanceStatus } from '@/api/types'
import { cn } from '@/lib/cn'
import { ATTENDANCE_ORDER, ATTENDANCE_SHORT } from '@/lib/dictionaries'

/**
 * Wybór obecności jednym kliknięciem.
 *
 * Świadomie bez okna dialogowego i bez przycisku „Zapisz”: nauczyciel rozlicza
 * dzień między lekcjami, często na telefonie, więc każdy dodatkowy krok to
 * realny koszt. Kliknięcie od razu wysyła żądanie, a cofnięcie pomyłki jest
 * równie tanie — wystarczy wybrać inny status.
 */

const TONY: Record<AttendanceStatus, { wybrany: string; nieaktywny: string }> = {
  PRESENT: {
    wybrany: 'bg-success text-white border-success shadow-raised',
    nieaktywny: 'ghost-border text-on-surface-variant hover:border-success hover:text-success',
  },
  ABSENT_NOTIFIED: {
    wybrany: 'bg-secondary text-on-secondary border-secondary shadow-raised',
    nieaktywny: 'ghost-border text-on-surface-variant hover:border-secondary hover:text-secondary',
  },
  // Wyróżnienie kolorem wprost z ustaleń ze szkołą: nieobecność bez zgłoszenia
  // co najmniej dobę wcześniej ma rzucać się w oczy.
  ABSENT_UNNOTIFIED: {
    wybrany: 'bg-error text-on-error border-error shadow-raised',
    nieaktywny: 'ghost-border text-on-surface-variant hover:border-error hover:text-error',
  },
}

interface AttendancePickerProps {
  value: AttendanceStatus | null
  onChange: (status: AttendanceStatus) => void
  /** Zajęcia jeszcze się nie odbyły — wolno zgłosić tylko nieobecność. */
  absenceOnly?: boolean
  disabled?: boolean
  /** Opis dla czytników ekranu, np. „Obecność — Anna Kowalska, 14:00”. */
  label: string
}

export function AttendancePicker({
  value,
  onChange,
  absenceOnly = false,
  disabled = false,
  label,
}: AttendancePickerProps) {
  const dostepne = absenceOnly
    ? ATTENDANCE_ORDER.filter((status) => status.startsWith('ABSENT'))
    : ATTENDANCE_ORDER

  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-2">
      {dostepne.map((status) => {
        const wybrany = value === status
        const tony = TONY[status]

        return (
          <button
            key={status}
            type="button"
            disabled={disabled}
            aria-pressed={wybrany}
            onClick={() => onChange(status)}
            className={cn(
              'rounded-full border px-4 py-2 text-xs font-semibold transition',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
              'focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50',
              wybrany ? tony.wybrany : cn('bg-surface', tony.nieaktywny),
            )}
          >
            {ATTENDANCE_SHORT[status]}
          </button>
        )
      })}
    </div>
  )
}
